import { resolveCustomerFieldMappings } from '@/config/shared/commerce';
import type { FormAutoSource } from '@/config/shared/auto-source';
import type { SelectorFormTrigger } from '@/config/shared/form';
import { DEFAULT_SELECTOR_TYPE } from '@/config/shared/selector';
import type { ConversionEventData } from '@/events/conversion';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BaseAutoTracker } from '../../auto/BaseAutoTracker';
import { buildAutoSourceOrigin } from '../../auto/build-auto-source-origin';
import {
  CustomerExtractor,
  FormConditionEvaluator,
  ProductExtractor,
} from '../../commerce';
import { isHtmlFormElement } from '../../commerce/extractor-utils';
import type { ConversionEventOrigin } from '../../contracts';
import { SelectorResolver } from '../SelectorResolver';
import type { TrackerFactory } from '../../tracks/TrackerFactory';

export type FormCapableEventType = 'LEAD' | 'ADD_TO_CART' | 'INITIATE_CHECKOUT';

export type ConfiguredFormOptions = {
  readonly eventType: FormCapableEventType;
  readonly source: FormAutoSource;
  readonly origin?: ConversionEventOrigin;
};

/**
 * Detector de source `type: 'form'`: escuta DOM, avalia condição e dispara via TrackerFactory.
 * Não usa CommerceResolver / OrderCalculator.
 * Não chama Dispatcher diretamente.
 */
export class ConfiguredFormDetector extends BaseAutoTracker {
  private readonly factory: TrackerFactory;

  private readonly options: ConfiguredFormOptions;

  private readonly formEvaluator: FormConditionEvaluator;

  private readonly selectorResolver: SelectorResolver;

  private readonly customerExtractor: CustomerExtractor;

  private readonly productExtractor: ProductExtractor;

  private readonly hasSelector: boolean;

  private readonly selectorType: ReturnType<typeof resolveSelectorType>;

  private readonly debounceMs: number;

  private readonly fireOnce: boolean;

  private readonly origin: ConversionEventOrigin;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private hasFired = false;

  private destroyed = false;

  constructor(
    context: PixelRuntimeContext,
    factory: TrackerFactory,
    options: ConfiguredFormOptions
  ) {
    super(context);
    this.factory = factory;
    this.options = options;

    const { source } = options;
    this.formEvaluator = new FormConditionEvaluator(source.selectorForm);
    this.selectorResolver = new SelectorResolver(context.logger);
    this.hasSelector = this.selectorResolver.hasEntries(source.selector);
    this.selectorType = resolveSelectorType(source);
    this.origin =
      options.origin ??
      buildAutoSourceOrigin('form', options.eventType, source.name);

    const commerce = context.services.commerce.config;
    this.customerExtractor = new CustomerExtractor({
      fieldMappings: resolveCustomerFieldMappings(
        commerce?.customer,
        commerce?.mappings?.fields
      ),
    });
    this.productExtractor = new ProductExtractor({
      attributeMappings: commerce?.mappings?.attributes,
      fieldMappings: commerce?.mappings?.fields,
    });

    const normalized = this.formEvaluator.getConfig();
    const sourceDebounce = source.debounceMs;
    const formDebounce = normalized?.debounceMs;
    this.debounceMs =
      typeof sourceDebounce === 'number' && sourceDebounce > 0
        ? sourceDebounce
        : typeof formDebounce === 'number' && formDebounce > 0
          ? formDebounce
          : 0;
    this.fireOnce = source.fireOnce === true || normalized?.fireOnce === true;
  }

  get name(): string {
    return `ConfiguredForm(${this.options.eventType}:${this.options.source.name})`;
  }

  protected onInit(): void {
    this.destroyed = false;
    this.hasFired = false;
    this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);

    if (!this.isDocumentAvailable()) return;

    const trigger = this.formEvaluator.getConfig()?.trigger ?? 'auto';
    const events = this.resolveDomEvents(trigger);

    for (const type of events) {
      this.addDocumentListener(
        type,
        event => this.handleDomEvent(event, type),
        true
      );
    }
  }

  protected onDestroy(): void {
    this.destroyed = true;
    this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
  }

  private resolveDomEvents(
    trigger: SelectorFormTrigger
  ): Array<'input' | 'change' | 'submit' | 'click'> {
    if (trigger === 'auto') {
      return ['input', 'change', 'submit', 'click'];
    }
    return [trigger];
  }

  private handleDomEvent(
    event: Event,
    domType: 'input' | 'change' | 'submit' | 'click'
  ): void {
    if (this.destroyed) return;
    if (this.fireOnce && this.hasFired) return;

    const trigger = domType as SelectorFormTrigger;
    const element = event.target instanceof Element ? event.target : null;

    // Selector/formSelector: restringe TODOS os triggers ao escopo.
    if (!this.isEventInScope(element, trigger)) return;

    const evaluation = this.formEvaluator.evaluate({
      element,
      trigger,
    });

    if (!evaluation.matched) {
      this.context.logger.debug(
        `[ConfiguredFormDetector] ${this.options.eventType} skipped: ${evaluation.reason}`
      );
      return;
    }

    const data = this.buildPayload(element);
    if (data === null) return;

    this.scheduleTrack(data);
  }

  private isEventInScope(
    element: Element | null,
    trigger: SelectorFormTrigger
  ): boolean {
    if (this.hasSelector) {
      if (!this.matchesSelector(element)) return false;
    }

    const formSelector = this.formEvaluator.getConfig()?.formSelector;
    if (formSelector && typeof document !== 'undefined') {
      try {
        const form = document.querySelector(formSelector);
        if (!form) return false;
        if (!element) return trigger !== 'click';
        if (form !== element && !form.contains(element)) return false;
      } catch {
        return false;
      }
    }

    return true;
  }

  private matchesSelector(element: Element | null): boolean {
    if (!element || !this.options.source.selector) return false;

    let current: Element | null = element;
    while (current && current !== document.body) {
      if (
        this.selectorResolver.matches(
          current,
          this.options.source.selector,
          this.selectorType
        )
      ) {
        return true;
      }
      current = current.parentElement;
    }
    return false;
  }

  private resolveCustomerRoot(element: Element | null): Element | null {
    const formSelector = this.formEvaluator.getConfig()?.formSelector;
    if (formSelector && typeof document !== 'undefined') {
      try {
        const found = document.querySelector(formSelector);
        if (found) return found;
      } catch {
        // CSS inválido — fallback abaixo
      }
    }

    return element;
  }

  private buildPayload(element: Element | null): ConversionEventData | null {
    const { eventType } = this.options;

    if (eventType === 'LEAD') {
      const root = this.resolveCustomerRoot(element);
      const customer = isHtmlFormElement(root)
        ? this.customerExtractor.extractFromForm(root)
        : this.customerExtractor.extractFromElement(root);
      if (!customer.email && !customer.phone && !customer.name) {
        this.context.logger.debug(
          '[ConfiguredFormDetector] LEAD skipped: no email/phone/name'
        );
        return null;
      }
      return {
        ...(customer.email ? { email: customer.email } : {}),
        ...(customer.phone ? { phone: customer.phone } : {}),
        ...(customer.name ? { name: customer.name } : {}),
      };
    }

    if (eventType === 'ADD_TO_CART') {
      const product = this.productExtractor.extractFromElement(element);
      return {
        ...(product.productId ? { productId: product.productId } : {}),
        ...(product.productName ? { productName: product.productName } : {}),
        ...(product.price != null ? { price: product.price } : {}),
        ...(product.currency ? { currency: product.currency } : {}),
        ...(product.quantity != null ? { quantity: product.quantity } : {}),
      };
    }

    return {};
  }

  private scheduleTrack(data: ConversionEventData): void {
    if (this.debounceMs <= 0) {
      this.track(data);
      return;
    }

    this.debounceTimer = this.clearManagedTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.destroyed) return;
      this.track(data);
    }, this.debounceMs);
  }

  private track(data: ConversionEventData): void {
    if (this.destroyed) return;
    if (this.fireOnce && this.hasFired) return;

    this.factory.trackAuto(this.options.eventType, data, {
      origin: this.origin,
    });

    if (this.fireOnce) {
      this.hasFired = true;
    }
  }
}

function resolveSelectorType(source: FormAutoSource) {
  return source.selectorType ?? DEFAULT_SELECTOR_TYPE;
}
